/*
 * <BeTrackerClock> -- the live time readout in the Deskbar's Status View.
 *
 * NOT the BeOS Clock application. That is a separate standalone app (its own
 * window, analog face + settings) and is still to be built -- kanban #389, which
 * was split out of the old #102 epic precisely because the two were conflated.
 * This component is #102's widget: the Deskbar strip's `h:mm AM/PM` text.
 *
 * Upstream this is `TTimeView` (OpenTracker deskbar/TimeView.cpp), a replicant
 * in the Deskbar's status shelf -- Tracker's clock, not an app.
 *
 * Defaults to the R5 Deskbar's 12-hour format. Ticks once a minute (the Deskbar
 * clock has no seconds by default).
 */

import { useEffect, useState } from "react";
import { type BeProps, beComponent } from "./create.tsx";

export type BeTrackerClockProps = BeProps<
	"time",
	{
		/**
		 * Formatter for the current time. Defaults to 12-hour "h:mm AM/PM".
		 * Override to show seconds, 24-hour, a date, etc.
		 */
		format?: (now: Date) => string;
	}
>;

function defaultFormat(now: Date): string {
	return now.toLocaleTimeString(undefined, {
		hour: "numeric",
		minute: "2-digit",
	});
}

export const BeTrackerClock = beComponent<BeTrackerClockProps, HTMLTimeElement>("BeTrackerClock", (props, be) => {
	const { format = defaultFormat, scale, className, style, ...rest } = props;
	const [now, setNow] = useState(() => new Date());

	useEffect(() => {
		// Re-tick at the top of each minute, then every minute after.
		let interval: ReturnType<typeof setInterval>;
		const msToNextMinute = 60_000 - (Date.now() % 60_000);
		const timeout = setTimeout(() => {
			setNow(new Date());
			interval = setInterval(() => setNow(new Date()), 60_000);
		}, msToNextMinute);
		return () => {
			clearTimeout(timeout);
			clearInterval(interval);
		};
	}, []);

	return (
		<time {...rest} {...be("be-deskbar__clock", "be-pixel-text")}>
			{format(now)}
		</time>
	);
});
